import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ImpactBadge, StatusBadge } from "@/components/Badges";
import { track } from "@/lib/analytics";
import { FixSuggestion } from "@shared/ui/FixSuggestion";
import "@shared/ui/fix-suggestion.css";

const STATUSES = ["open", "fixed", "wont_fix", "false_positive"];

/* Each success criterion renders its own table. Left to size themselves, every
 * table picks column widths from its own rows, so Impact and Selector land at a
 * different x position in each group and the page reads as ragged. A fixed
 * layout plus one shared column set makes every group line up, whatever its
 * content. Percentages, so the grid still adapts to the viewport. */
const COLUMNS = [
  { key: "rule", label: "Rule", width: "32%" },
  { key: "impact", label: "Impact", width: "10%" },
  { key: "selector", label: "Selector", width: "26%" },
  { key: "status", label: "Status", width: "13%" },
  { key: "set-status", label: "Set status", width: "19%" },
];

const IssueColgroup = () => (
  <colgroup>
    {COLUMNS.map((c) => <col key={c.key} style={{ width: c.width }} />)}
  </colgroup>
);

export default function IssuesTab({ siteId, onChanged }) {
  const [issues, setIssues] = useState(null);
  // Suggested fixes are a Solo and Agency feature; the plan gates the block.
  const [plan, setPlan] = useState(null);

  const load = useCallback(() => {
    api.get(`/sites/${siteId}/issues`).then((r) => setIssues(r.data)).catch(() => toast.error("Could not load issues"));
  }, [siteId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/me").then((r) => setPlan(r.data.plan)).catch(() => setPlan("free"));
  }, []);
  const paid = plan === "solo" || plan === "agency";

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/issues/${id}`, { status });
      track("issue_status_changed", { status });
      toast.success(`Status set to ${status.replace("_", " ")}`);
      load();
      onChanged?.();
    } catch {
      toast.error("Could not update status");
    }
  };

  if (!issues) return <p className="text-sm text-ink-muted font-mono">Loading...</p>;
  if (issues.length === 0) {
    return <p className="text-sm text-ink-muted py-10 text-center border border-dashed border-gray-300 rounded-sm bg-white" data-testid="issues-empty">No issues recorded for this site yet.</p>;
  }

  // group by primary success criterion, mirroring how conformance reports read
  const groups = {};
  for (const i of issues) {
    const key = i.wcag_criteria[0] || "unmapped";
    (groups[key] = groups[key] || []).push(i);
  }
  const orderedKeys = Object.keys(groups).sort();

  return (
    <div data-testid="issues-list">
      {orderedKeys.map((key) => (
        <section key={key} className="mb-6" aria-labelledby={`crit-${key}`}>
          <h2 id={`crit-${key}`} className="font-heading text-sm font-semibold mb-2">
            <span className="font-mono text-xs mr-2 text-[#16324F]">{key === "unmapped" ? "Best practice" : `SC ${key}`}</span>
            {key !== "unmapped" && (groups[key][0].criteria_names?.[key] || "")}
          </h2>
          <div className="table-card" tabIndex={0} role="region" aria-label={`Issues for criterion ${key}`}>
            {/* min-width keeps the fixed grid legible on narrow viewports: the
                wrapper scrolls rather than crushing five columns together. */}
            <table className="w-full min-w-[880px] table-fixed text-left border-collapse text-sm">
              <caption className="sr-only">Issues for criterion {key}</caption>
              <IssueColgroup />
              <thead>
                <tr className="border-b border-gray-300">
                  {COLUMNS.map((c) => (
                    <th key={c.key} scope="col" className="px-4 py-2.5 font-heading font-semibold">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups[key].map((i) => (
                  <tr key={i.id} className="border-b border-gray-200 last:border-0 align-top" data-testid={`issue-row-${i.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-mono text-xs font-medium">{i.rule_id}</p>
                      {/* No max-width: the fixed column already sets the measure,
                          and a narrower cap would wrap text short of the cell. */}
                      <p className="text-xs text-ink-muted mt-1">{i.description}</p>
                      {i.help_url && (
                        <a href={i.help_url} target="_blank" rel="noreferrer"
                          className="text-xs text-[#16324F] underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]">
                          Remediation guidance
                        </a>
                      )}
                      <details className="mt-2" data-testid={`issue-fix-details-${i.id}`}>
                        <summary className="text-xs text-[#16324F] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] rounded-sm">
                          Suggested fix
                        </summary>
                        <div className="mt-2">
                          {paid ? (
                            <FixSuggestion finding={i} />
                          ) : (
                            <p className="text-xs text-ink-muted" data-testid="issue-fix-upsell">
                              Suggested code fixes (cause plus a before and after pattern) are part of the
                              Solo and Agency plans.
                            </p>
                          )}
                        </div>
                      </details>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap"><ImpactBadge impact={i.impact} /></td>
                    <td className="px-4 py-3">
                      <code className="font-mono text-xs break-all block">{i.selector_display}</code>
                      <p className="font-mono text-[10px] text-ink-subtle mt-1">tier: {i.selector_tier}</p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap"><StatusBadge status={i.status} /></td>
                    <td className="px-4 py-3">
                      <label htmlFor={`status-${i.id}`} className="sr-only">Set status for {i.rule_id}</label>
                      <select
                        id={`status-${i.id}`}
                        data-testid={`issue-status-select-${i.id}`}
                        value={i.status}
                        onChange={(e) => setStatus(i.id, e.target.value)}
                        className="w-full border border-gray-300 rounded-sm px-2 py-1 text-xs bg-white focus:ring-2 focus:ring-[#16324F] outline-none"
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
